// Thin fetch-based API client for the FlLink.Api backend. Every function
// returns already-parsed JSON matching the shapes the app's components
// expect (ClientRecordDto on the backend mirrors ClientRecord here).
import type { ClientRecord, PendingBucketKey } from "../types";

const BASE_URL = (window as any)?.REACT_APP_API_URL ?? "http://localhost:5000/api";

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`API ${options?.method ?? "GET"} ${path} failed: ${res.status} ${body}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/* ------------------------------- Clients / cases ------------------------------- */

export function fetchCases(params?: { keyword?: string; bucket?: PendingBucketKey | null }) {
  const qs = new URLSearchParams();
  if (params?.keyword) qs.set("keyword", params.keyword);
  if (params?.bucket) qs.set("bucket", params.bucket);
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<ClientRecord[]>(`/clients${suffix}`);
}

export function searchClients(params: {
  source?: string; firstName: string; lastName: string; dob: string; birthSex?: string; wildcard?: boolean;
}) {
  const qs = new URLSearchParams();
  if (params.source) qs.set("source", params.source);
  qs.set("firstName", params.firstName);
  qs.set("lastName", params.lastName);
  qs.set("dob", params.dob);
  if (params.birthSex) qs.set("birthSex", params.birthSex);
  if (params.wildcard) qs.set("wildcard", "true");
  return request<ClientRecord[]>(`/clients/search?${qs}`);
}

export function fetchClient(id: string) {
  return request<ClientRecord>(`/clients/${id}`);
}

export function createClient(record: ClientRecord) {
  return request<ClientRecord>("/clients", { method: "POST", body: JSON.stringify(record) });
}

export function updateClient(id: string, record: ClientRecord) {
  return request<ClientRecord>(`/clients/${id}`, { method: "PUT", body: JSON.stringify(record) });
}

/* -------------------------------- Support tickets ------------------------------- */

export interface SupportTicketApi {
  id?: number;
  requestType: string;
  description: string;
  status: string;
  assignedTo?: string;
  facilityName?: string;
  requestSource: string;
  priority: string;
  requestorName?: string;
  phoneNumber?: string;
  emailAddress?: string;
  canContactByPhone: boolean;
  dateAdded?: string;
  lastUpdated?: string;
  notes: { text: string; createdDate: string }[];
}

export function fetchSupportTickets(status?: string) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return request<SupportTicketApi[]>(`/support-tickets${qs}`);
}
export function fetchSupportTicket(id: number) {
  return request<SupportTicketApi>(`/support-tickets/${id}`);
}
export function createSupportTicket(ticket: SupportTicketApi) {
  return request<SupportTicketApi>("/support-tickets", { method: "POST", body: JSON.stringify(ticket) });
}
export function updateSupportTicket(id: number, ticket: SupportTicketApi) {
  return request<SupportTicketApi>(`/support-tickets/${id}`, { method: "PUT", body: JSON.stringify(ticket) });
}
export function addSupportTicketNote(id: number, text: string) {
  return request<SupportTicketApi>(`/support-tickets/${id}/notes`, { method: "POST", body: JSON.stringify({ text }) });
}

/* ------------------------------------ Reports ------------------------------------ */

export interface GeneratedReportApi {
  id?: number;
  name: string;
  type: string;
  dateRangeFrom?: string;
  dateRangeTo?: string;
  generatedDate?: string;
  generatedBy?: string;
  format: string;
}

export function fetchGeneratedReports() {
  return request<GeneratedReportApi[]>("/reports");
}
export function generateReport(params: { reportType: string; dateFrom: string; dateTo: string; facility?: string; format: string }) {
  return request<GeneratedReportApi>("/reports/generate", { method: "POST", body: JSON.stringify(params) });
}

/* -------------------------------- Lookup / dashboard ------------------------------ */

export function fetchHospitals() {
  return request<{ id: number; name: string; facilities: string[] }[]>("/lookup/hospitals");
}
export function fetchStaff() {
  return request<{ id: number; displayName: string }[]>("/lookup/staff");
}
export function fetchDashboardKpis() {
  return request<{
    processed: number; totalCases: number; closed: number; closureRate: number;
    hivLinked: number; linkageBreakdown: { label: string; value: number }[];
  }>("/dashboard/kpis");
}
